import { minutesToHours } from '@/helpers/date-format';
import { DEFAULT_COLOR_VALUES } from '@/lib/constants/colors';
import { normalizeColorHex } from '@/lib/utils';
import { TimeOffStatus } from '@/modules/time-off/types/time-off-status';

import {
  getApprovalMemberRowId,
  getApprovalProjectRowId,
} from '../helpers/approval-row-model.helper';
import {
  ApprovalProjectItemResponse,
  ApprovalSelectionRequest,
  ApprovalSummaryItemResponse,
  ApprovalTeamMemberItemResponse,
  ApprovalWorklogItemResponse,
  ApprovalWorklogRowTypeDTO,
  ApproveByProjectRequest,
  ApproveByTeamMemberRequest,
  ApproveByWorklogRequest,
} from '../types/dto/worklogs-approval.dto';
import {
  ApprovalConfirmMode,
  ApprovalEditedEntries,
  ApprovalEntryRow,
  ApprovalExclusionKind,
  ApprovalMemberRow,
  ApprovalProjectRow,
  ApprovalSelection,
  ApprovalSelectionLevel,
  ApprovalsSummary,
  ApprovalTimeEntry,
  ApprovalTimeOffEntry,
  TimeEntryStatus,
} from '../types/time-tracking-approvals';

/** Rounds a numeric value to two decimal places (hours / percentages shown to the user). */
const roundToTwoDecimals = (value: number): number => Math.round(value * 100) / 100;

export interface ApprovalProjectContext {
  projectId: number;
  projectName: string;
  projectColorHex: string;
  projectHash: string;
}

export interface ApprovalMemberContext {
  memberId: number;
  memberName: string;
  memberAvatarUrl: string | null;
  memberHash: string;
}

const mapApprovalWorklogStatus = (
  status: ApprovalWorklogItemResponse['approvalStatus']
): TimeEntryStatus =>
  status === 'APPROVED' ? TimeEntryStatus.APPROVED : TimeEntryStatus.SUBMITTED;

/**
 * The backend rate-masking interceptor replaces money values with -1 for users
 * without the billing permission (columns are hidden then, but be defensive) —
 * negative money values are never legitimate here.
 */
const sanitizeMaskedAmount = (value: number | null | undefined): number | null =>
  value == null || value < 0 ? null : value;

/**
 * Maps the approval summary response to the domain summary shown in the top stat cards.
 * The `rateApprovedFor*ToBooked` fields arrive as 0–1 ratios (→ percentages); income values are
 * masked to -1 for users without the billing permission and normalized to null via the shared
 * `sanitizeMaskedAmount`.
 */
export const mapApprovalSummaryFromResponse = (
  response: ApprovalSummaryItemResponse
): ApprovalsSummary => ({
  awaitingApprovalHours: roundToTwoDecimals(response.totalHoursAwaitingApproval),
  approvedForInvoiceHours: roundToTwoDecimals(response.totalApprovedForInvoiceHours),
  approvedForBillHours: roundToTwoDecimals(response.totalApprovedForBillHours),
  entriesPending: response.numberOfEntriesPending,
  invoiceApprovalPercentage: Math.round(response.rateApprovedForInvoiceToBooked * 100),
  billApprovalPercentage: Math.round(response.rateApprovedForBillToBooked * 100),
  totalWorklogs: response.totalWorklogs,
  trackedHours: roundToTwoDecimals(response.totalTrackedHours ?? 0),
  bookedHours: roundToTwoDecimals(response.totalBookedHours ?? 0),
  approvedIncome: sanitizeMaskedAmount(response.approvedIncome),
  pendingIncome: sanitizeMaskedAmount(response.pendingIncome),
  currencyCode: response.currencyCode ?? null,
});

export const mapApprovalProjectItemToRow = (
  project: ApprovalProjectItemResponse
): ApprovalProjectRow => ({
  id: getApprovalProjectRowId(project.id),
  project: {
    projectId: project.id,
    projectName: project.name,
    logoUrl: project.logoUrl ?? null,
    colorHex:
      normalizeColorHex(project.colorHex) ??
      DEFAULT_COLOR_VALUES[project.id % DEFAULT_COLOR_VALUES.length],
  },
  trackedHours: minutesToHours(project.trackedMinutes),
  pendingTrackedHours: minutesToHours(project.pendingTrackedMinutes),
  bookedHours: minutesToHours(project.bookedMinutes),
  approvedForInvoiceHours: minutesToHours(project.approvedForInvoiceMinutes),
  approvedForBillHours: minutesToHours(project.approvedForBillMinutes),
  pendingApprovedForInvoiceHours: minutesToHours(project.pendingApprovedForInvoiceMinutes),
  pendingApprovedForBillHours: minutesToHours(project.pendingApprovedForBillMinutes),
  approvalPercentage: project.approvalPercentage,
  totalWorklogs: project.totalWorklogs,
  approvedWorklogs: project.approvedWorklogs,
  pendingWorklogs: project.pendingWorklogs,
  hasEmptyDescription: project.hasEmptyDescription,
  timeOffHours: minutesToHours(project.timeOffMinutes),
  approvedTimeOffHours: minutesToHours(project.approvedTimeOffMinutes),
  pendingTimeOffHours: minutesToHours(project.pendingTimeOffMinutes),
  externalRate: sanitizeMaskedAmount(project.externalRate),
  approvedIncome: sanitizeMaskedAmount(project.approvedIncome),
  currencyCode: project.currencyCode ?? null,
  members: [],
  hasChildren: true,
  hash: project.hash,
});

export const mapApprovalTeamMemberToRow = (
  member: ApprovalTeamMemberItemResponse,
  projectId: number
): ApprovalMemberRow => ({
  id: getApprovalMemberRowId(projectId, member.id),
  member: { id: member.id, name: member.name, avatarUrl: member.profileImageUrl ?? null },
  trackedHours: minutesToHours(member.trackedMinutes),
  pendingTrackedHours: minutesToHours(member.pendingTrackedMinutes),
  bookedHours: minutesToHours(member.bookedMinutes),
  approvedForInvoiceHours: minutesToHours(member.approvedForInvoiceMinutes),
  approvedForBillHours: minutesToHours(member.approvedForBillMinutes),
  pendingApprovedForInvoiceHours: minutesToHours(member.pendingApprovedForInvoiceMinutes),
  pendingApprovedForBillHours: minutesToHours(member.pendingApprovedForBillMinutes),
  approvalPercentage: member.approvalPercentage,
  totalWorklogs: member.totalWorklogs,
  approvedWorklogs: member.approvedWorklogs,
  pendingWorklogs: member.pendingWorklogs,
  hasEmptyDescription: member.hasEmptyDescription,
  timeOffHours: minutesToHours(member.timeOffMinutes),
  approvedTimeOffHours: minutesToHours(member.approvedTimeOffMinutes),
  pendingTimeOffHours: minutesToHours(member.pendingTimeOffMinutes),
  externalRate: sanitizeMaskedAmount(member.externalRate),
  approvedIncome: sanitizeMaskedAmount(member.approvedIncome),
  currencyCode: member.currencyCode ?? null,
  timeEntries: [],
  hasChildren: true,
  hash: member.hash,
});

const mapApprovalWorklogToTimeOffEntry = (
  wl: ApprovalWorklogItemResponse
): ApprovalTimeOffEntry => ({
  id: `timeoff-${wl.id}`,
  date: wl.date,
  timeOffTypeName: wl.timeOffTypeName ?? '',
  durationHours: minutesToHours(wl.durationMinutes ?? 0),
  status: wl.approvalStatus === 'APPROVED' ? TimeOffStatus.Approved : TimeOffStatus.Pending,
  description: wl.description,
});

export const mapApprovalWorklogToTimeEntry = (
  wl: ApprovalWorklogItemResponse,
  projectContext: ApprovalProjectContext,
  memberContext: ApprovalMemberContext
): ApprovalTimeEntry => ({
  id: wl.id,
  date: wl.date,
  trackedHours: minutesToHours(wl.trackedMinutes ?? 0),
  // Each value falls back to tracked minutes independently — a null override means
  // "approved at tracked minutes" for that purpose only.
  approvedForInvoiceHours: minutesToHours(wl.approvedForInvoiceMinutes ?? wl.trackedMinutes ?? 0),
  approvedForBillHours: minutesToHours(wl.approvedForBillMinutes ?? wl.trackedMinutes ?? 0),
  approvalNotes: wl.approvalNotes,
  description: wl.description ?? '',
  status: mapApprovalWorklogStatus(wl.approvalStatus),
  projectId: projectContext.projectId,
  projectName: projectContext.projectName,
  projectColorHex: projectContext.projectColorHex,
  projectHash: projectContext.projectHash,
  memberId: memberContext.memberId,
  memberName: memberContext.memberName,
  memberAvatarUrl: memberContext.memberAvatarUrl,
  memberHash: memberContext.memberHash,
  externalRate: sanitizeMaskedAmount(wl.externalRate),
  approvedIncome: sanitizeMaskedAmount(wl.approvedIncome),
  currencyCode: wl.currencyCode ?? null,
  hash: wl.hash ?? '',
});

/**
 * Worklog-level approve item: carries the entry's (possibly edited) approved hours + notes.
 * Both approved values ride along — this is the only request shape that can express them.
 */
const mapEntryToApproveWorklogRequest = (entry: ApprovalTimeEntry): ApproveByWorklogRequest => ({
  id: entry.id,
  hash: entry.hash,
  approvedForInvoiceMinutes: Math.round(entry.approvedForInvoiceHours * 60),
  approvedForBillMinutes: Math.round(entry.approvedForBillHours * 60),
  approvalNotes: entry.approvalNotes ?? undefined,
});

/**
 * Entries of an aggregate (project/member) selection that carry pending inline edits.
 *
 * An aggregate request item is only `{id, hash, excluded…}` — it CANNOT express per-entry
 * approved minutes/notes (for either value), so the backend would approve those entries at their
 * tracked hours and the user's edits would be silently dropped. They are therefore split out:
 * excluded from the aggregate item and sent as worklog-level items in the same request.
 *
 * Unapprove ignores them: it clears approval data, and inline edits only ever exist on
 * still-pending entries, which an unapprove doesn't touch.
 */
const getEditedEntriesToSplit = (
  editedEntries: ApprovalEditedEntries,
  mode: ApprovalConfirmMode
): ApprovalTimeEntry[] =>
  mode === ApprovalConfirmMode.Approve ? editedEntries.map(({ entry }) => entry) : [];

/**
 * Maps a selection to the approve/unapprove request body. One selection LEVEL drives the
 * request; the approved minutes and notes can only ride on worklog items, so an aggregate
 * selection with pending inline edits is sent as the aggregate item (with those entries
 * excluded) PLUS one worklog item per edited entry — see `getEditedEntriesToSplit`.
 */
export const mapApprovalSelectionToRequest = (
  selection: ApprovalSelection,
  mode: ApprovalConfirmMode
): ApprovalSelectionRequest => {
  switch (selection.level) {
    case ApprovalSelectionLevel.Projects: {
      const worklogs: ApproveByWorklogRequest[] = [];
      const projects = selection.projects.map(({ project, exclusions, editedEntries }) => {
        const splitEntries = getEditedEntriesToSplit(editedEntries, mode);
        worklogs.push(...splitEntries.map(mapEntryToApproveWorklogRequest));

        const excludedWorklogIds = [
          ...exclusions
            .filter(exclusion => exclusion.kind === ApprovalExclusionKind.Entry)
            .map(exclusion => exclusion.entryId),
          ...splitEntries.map(entry => entry.id),
        ];
        const excludedUserIds = exclusions
          .filter(exclusion => exclusion.kind === ApprovalExclusionKind.Member)
          .map(exclusion => exclusion.userId);

        const item: ApproveByProjectRequest = {
          id: project.project.projectId,
          hash: project.hash,
        };
        if (excludedWorklogIds.length > 0) item.excludedWorklogIds = excludedWorklogIds;
        if (excludedUserIds.length > 0) item.excludedUserIds = excludedUserIds;
        return item;
      });

      const request: ApprovalSelectionRequest = { projects };
      if (worklogs.length > 0) request.worklogs = worklogs;
      return request;
    }
    case ApprovalSelectionLevel.Members: {
      const worklogs: ApproveByWorklogRequest[] = [];
      const teamMembers = selection.members.map(
        ({ member, projectId, exclusions, editedEntries }) => {
          const splitEntries = getEditedEntriesToSplit(editedEntries, mode);
          worklogs.push(...splitEntries.map(mapEntryToApproveWorklogRequest));

          const excludedWorklogIds = [
            ...exclusions.map(exclusion => exclusion.entryId),
            ...splitEntries.map(entry => entry.id),
          ];

          const item: ApproveByTeamMemberRequest = {
            projectId,
            id: member.member.id,
            hash: member.hash,
          };
          if (excludedWorklogIds.length > 0) item.excludedWorklogIds = excludedWorklogIds;
          return item;
        }
      );

      const request: ApprovalSelectionRequest = { teamMembers };
      if (worklogs.length > 0) request.worklogs = worklogs;
      return request;
    }
    case ApprovalSelectionLevel.Worklogs:
      return {
        worklogs: selection.entries.map(entry =>
          mode === ApprovalConfirmMode.Approve
            ? mapEntryToApproveWorklogRequest(entry)
            : { id: entry.id, hash: entry.hash }
        ),
      };
  }
};

/** Maps a row of the approval worklogs list to its leaf row shape based on the `rowType` discriminator. */
export const mapApprovalWorklogToEntryRow = (
  wl: ApprovalWorklogItemResponse,
  projectContext: ApprovalProjectContext,
  memberContext: ApprovalMemberContext
): ApprovalEntryRow =>
  wl.rowType === ApprovalWorklogRowTypeDTO.TIME_OFF
    ? mapApprovalWorklogToTimeOffEntry(wl)
    : mapApprovalWorklogToTimeEntry(wl, projectContext, memberContext);
